import React from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./RegisterOssCompanyDetailsPage.css";
import PasswordStrengthChecklist, {
  isStrongPassword,
} from "../../../../components/PasswordStrengthChecklist";
import { Eye, EyeOff } from "lucide-react";
import GoogleVerifiedAddressInput from "../../../../components/maps/GoogleVerifiedAddressInput";

const ACCESS_STORAGE_KEY = "legup_oss_access_context";
const API_BASE = process.env.REACT_APP_API_BASE_URL;
const COMPANY_DETAILS_STORAGE_KEY = "legup_oss_company_details";

async function checkCompanyAddressAvailability({
  email,
  accessCode,
  placeId,
  signal,
}) {
  const response = await fetch(`${API_BASE}/api/oss/company-address/check/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    signal,
    body: JSON.stringify({
      email,
      access_code: accessCode,
      company_address_place_id: placeId,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      payload?.detail ||
      payload?.company_address?.[0] ||
      payload?.company_address_place_id?.[0] ||
      payload?.access_code?.[0] ||
      "Could not verify whether this office address is already registered.";
    throw new Error(
      typeof detail === "string" ? detail : "Address verification failed.",
    );
  }
  return payload;
}

const DEFAULT_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  company_name: "",
  password: "",
  confirm_password: "",
  company_address: "",
  company_address_place_id: "",
  country_code: "",
  vat_number: "",
  role_in_company: "",
  phone: "",
  additional_notes: "",
};

export default function RegisterOssCompanyDetailsPage() {
  const { t } = useTranslation("oss-registration-company-details");
  const navigate = useNavigate();
  const { ossPassword, setOssPassword } = useOutletContext();
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const accessContext = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(ACCESS_STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  }, []);

  const preview = accessContext.preview || {};

  const prefilledFields = React.useMemo(
    () => ({
      first_name: !!preview.first_name,
      last_name: !!preview.last_name,
      email: !!(preview.email || accessContext.email),
      company_name: !!preview.company_name,
      country_code: !!preview.country_code,
      role_in_company: !!preview.role_in_company,
      phone: !!preview.phone,
      additional_notes: !!preview.message,
    }),

    [preview, accessContext.email],
  );

  const [form, setForm] = React.useState(() => {
    const initialForm = {
      ...DEFAULT_FORM,
      first_name: preview.first_name || "",
      last_name: preview.last_name || "",
      email: preview.email || accessContext.email || "",
      company_name: preview.company_name || "",
      country_code: preview.country_code || "",
      role_in_company: preview.role_in_company || "",
      phone: preview.phone || "",
      additional_notes: preview.message || "",
    };

    try {
      const saved = JSON.parse(
        localStorage.getItem(COMPANY_DETAILS_STORAGE_KEY) || "null",
      );

      const sameRequest =
        saved?.join_request_id != null &&
        preview?.join_request_id != null &&
        String(saved.join_request_id) === String(preview.join_request_id);

      if (!sameRequest) {
        return {
          ...initialForm,
          password: ossPassword || "",
          confirm_password: "",
        };
      }

      return {
        ...initialForm,
        ...saved,
        password: ossPassword || "",
        confirm_password: "",
      };
    } catch {
      return {
        ...initialForm,
        password: ossPassword || "",
        confirm_password: "",
      };
    }
  });

  const [fieldErrors, setFieldErrors] = React.useState({});
  const [err, setErr] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const [addressAvailability, setAddressAvailability] = React.useState({
    placeId: "",
    status: "idle", // idle | checking | available | duplicate | error
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    const placeId = String(form.company_address_place_id || "").trim();
    if (!placeId || !form.country_code) {
      setAddressAvailability({ placeId: "", status: "idle", message: "" });
      return undefined;
    }

    const controller = new AbortController();
    setAddressAvailability({ placeId, status: "checking", message: "" });
    checkCompanyAddressAvailability({
      email: accessContext.email || form.email,
      accessCode: accessContext.access_code,
      placeId,
      signal: controller.signal,
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        const duplicate = result.available === false;
        setAddressAvailability({
          placeId,
          status: duplicate ? "duplicate" : "available",
          message: duplicate
            ? t(
                "validation.companyAddressAlreadyRegistered",
                "This address is already registered by another One-Stop Shop. Please choose another address or contact the platform administrator.",
              )
            : "",
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setAddressAvailability({
          placeId,
          status: "error",
          message: error.message,
        });
      });
    return () => controller.abort();
  }, [
    form.company_address_place_id,
    form.country_code,
    form.email,
    accessContext.email,
    accessContext.access_code,
    t,
  ]);

  React.useEffect(() => {
    async function loadPreviewFromBackend() {
      const email = accessContext.email;
      const accessCode = accessContext.access_code;

      if (!email || !accessCode) {
        setErr(t("messages.missingAccessContext"));
        return;
      }

      setLoadingPreview(true);

      try {
        const res = await fetch(`${API_BASE}/api/oss/access-code/preview/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            access_code: accessCode,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          setErr(
            data?.access_code?.[0] ||
              data?.access_code ||
              data?.detail ||
              t("messages.previewFailed"),
          );
          return;
        }

        const nextPreview = data;

        localStorage.setItem(
          ACCESS_STORAGE_KEY,
          JSON.stringify({
            ...accessContext,
            email,
            access_code: accessCode,
            preview: nextPreview,
            access_verified: true,
          }),
        );

        setForm((prev) => ({
          ...prev,
          first_name: nextPreview.first_name || prev.first_name,
          last_name: nextPreview.last_name || prev.last_name,
          email: nextPreview.email || email || prev.email,
          company_name: nextPreview.company_name || prev.company_name,
          country_code: nextPreview.country_code || prev.country_code,
          role_in_company: nextPreview.role_in_company || prev.role_in_company,
          phone: nextPreview.phone || prev.phone,
          additional_notes: nextPreview.message || prev.additional_notes,
        }));
      } catch (ex) {
        setErr(ex?.message || String(ex));
      } finally {
        setLoadingPreview(false);
      }
    }

    loadPreviewFromBackend();
  }, []);

  React.useEffect(() => {
    try {
      // Only persist non-sensitive company details.
      const { password, confirm_password, ...formToSave } = form;
      localStorage.setItem(
        COMPANY_DETAILS_STORAGE_KEY,
        JSON.stringify({
          ...formToSave,
          join_request_id: preview.join_request_id,
        }),
      );
    } catch (error) {
      console.error("Failed to save OSS company details:", error);
    }
  }, [form]);

  function onChange(e) {
    const { name, value } = e.target;
    if (prefilledFields[name]) return;

    let nextValue = value;

    if (name === "first_name" || name === "last_name") {
      nextValue = value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, "");
    }

    if (name === "phone") {
      nextValue = value.replace(/[^\d+ ]/g, "").slice(0, 20);
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
      ...(name === "country_code" && nextValue !== prev.country_code
        ? { company_address: "", company_address_place_id: "" }
        : {}),
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Invalidate a previously submitted password until the form is validated again.
    if (name === "password") setOssPassword("");
    if (err) setErr("");
  }

  function validateForm() {
    const errors = {};

    if (!form.first_name.trim()) errors.first_name = t("validation.firstName");
    if (!form.last_name.trim()) errors.last_name = t("validation.lastName");
    if (!form.email.trim()) errors.email = t("validation.email");
    if (!form.company_name.trim())
      errors.company_name = t("validation.company");
    if (!form.password) {
      errors.password = t("validation.password");
    } else if (!isStrongPassword(form.password)) {
      errors.password = t("validation.passwordWeak");
    }

    if (!form.confirm_password)
      errors.confirm_password = t("validation.confirmPassword");
    else if (form.password !== form.confirm_password)
      errors.confirm_password = t("validation.passwordMismatch");

    if (!form.company_address.trim()) {
      errors.company_address = t("validation.companyAddress");
    } else if (!form.company_address_place_id) {
      errors.company_address = t(
        "validation.companyAddressSelection",
        "Select a verified street address from the suggestions.",
      );
    }

    if (!form.country_code) errors.country_code = t("validation.country");
    if (!form.vat_number.trim()) errors.vat_number = t("validation.vat");
    if (!form.role_in_company.trim())
      errors.role_in_company = t("validation.role");
    if (!form.phone.trim()) errors.phone = t("validation.phone");

    return errors;
  }

  async function submit(e) {
    e.preventDefault();
    if (isSubmitting) return;

    const errors = validateForm();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setErr(t("messages.correctHighlighted"));
      return;
    }

    // ALWAYS check again before proceeding: the previous check may be stale.
    setIsSubmitting(true);
    setErr("");
    try {
      const placeId = form.company_address_place_id;
      const result = await checkCompanyAddressAvailability({
        email: accessContext.email || form.email,
        accessCode: accessContext.access_code,
        placeId,
      });
      if (result.available !== true) {
        const message = t(
          "validation.companyAddressAlreadyRegistered",
          "This address is already registered by another One-Stop Shop. Please choose another address or contact the platform administrator.",
        );
        setAddressAvailability({ placeId, status: "duplicate", message });
        setFieldErrors((prev) => ({ ...prev, company_address: message }));
        return;
      }

      setAddressAvailability({ placeId, status: "available", message: "" });
      // Password remains in the parent React state only, never localStorage.
      setOssPassword(form.password);
      const { password, confirm_password, ...safeCompanyDetails } = form;
      localStorage.setItem(
        COMPANY_DETAILS_STORAGE_KEY,
        JSON.stringify(safeCompanyDetails),
      );
      navigate("/register/oss/services");
    } catch (error) {
      setFieldErrors((prev) => ({ ...prev, company_address: error.message }));
      setErr(
        t(
          "messages.addressCheckFailed",
          "Could not verify the office address. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="oss-company-page">
      {loadingPreview && (
        <div className="oss-company-note">{t("messages.loadingPreview")}</div>
      )}
      <section className="oss-company-hero">
        <p className="oss-company-kicker">{t("hero.kicker")}</p>
        <h1>{t("hero.title")}</h1>
        <p>{t("hero.subtitle")}</p>
      </section>

      <div className="oss-company-note">{t("note")}</div>

      <form className="oss-company-card" onSubmit={submit}>
        <div className="oss-company-card-header">
          <div>
            <h2>{t("card.title")}</h2>
            <p>{t("card.subtitle")}</p>
          </div>
        </div>

        <div className="oss-company-grid">
          <div className="oss-company-field">
            <label>
              {t("fields.firstName")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="first_name"
              value={form.first_name}
              onChange={onChange}
              readOnly={prefilledFields.first_name}
              className={prefilledFields.first_name ? "oss-prefilled" : ""}
            />
            {fieldErrors.first_name && <span>{fieldErrors.first_name}</span>}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.lastName")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="last_name"
              value={form.last_name}
              onChange={onChange}
              readOnly={prefilledFields.last_name}
              className={prefilledFields.last_name ? "oss-prefilled" : ""}
            />
            {fieldErrors.last_name && <span>{fieldErrors.last_name}</span>}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.email")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              readOnly={prefilledFields.email}
              className={prefilledFields.email ? "oss-prefilled" : ""}
            />
            {fieldErrors.email && <span>{fieldErrors.email}</span>}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.companyName")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="company_name"
              value={form.company_name}
              onChange={onChange}
              readOnly={prefilledFields.company_name}
              className={prefilledFields.company_name ? "oss-prefilled" : ""}
            />
            {fieldErrors.company_name && (
              <span>{fieldErrors.company_name}</span>
            )}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.password")}
              <span className="oss-required">*</span>
            </label>

            <div className="oss-password-wrap">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={onChange}
                placeholder={t("placeholders.password")}
                className={
                  fieldErrors.password ? "oss-company-input-error" : ""
                }
              />

              <button
                type="button"
                className="oss-password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={
                  showPassword
                    ? t("fields.hidePassword", "Hide password")
                    : t("fields.showPassword", "Show password")
                }
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <PasswordStrengthChecklist password={form.password} />

            {fieldErrors.password && <span>{fieldErrors.password}</span>}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.confirmPassword")}
              <span className="oss-required">*</span>
            </label>
            <div className="oss-password-wrap">
              <input
                name="confirm_password"
                type={showConfirmPassword ? "text" : "password"}
                value={form.confirm_password}
                onChange={onChange}
                placeholder={t("placeholders.confirmPassword")}
                className={
                  fieldErrors.confirm_password ? "oss-company-input-error" : ""
                }
              />
              <button
                type="button"
                className="oss-password-toggle"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={
                  showConfirmPassword
                    ? t("fields.hidePassword", "Hide password")
                    : t("fields.showPassword", "Show password")
                }
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {fieldErrors.confirm_password && (
              <span>{fieldErrors.confirm_password}</span>
            )}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.companyAddress")}
              <span className="oss-required">*</span>
            </label>
            <GoogleVerifiedAddressInput
              value={form.company_address}
              countryCode={form.country_code}
              placeholder={t(
                "placeholders.companyAddress",
                "Search for your registered office address",
              )}
              invalid={Boolean(fieldErrors.company_address)}
              onChange={(address) => {
                setForm((prev) => ({
                  ...prev,
                  company_address: address,
                  company_address_place_id: "",
                }));
                setFieldErrors((prev) => ({ ...prev, company_address: "" }));
              }}
              onSelect={({ address, placeId }) => {
                setForm((prev) => ({
                  ...prev,
                  company_address: address,
                  company_address_place_id: placeId,
                }));
                setFieldErrors((prev) => ({ ...prev, company_address: "" }));
              }}
            />
            {addressAvailability.placeId === form.company_address_place_id &&
              addressAvailability.status === "checking" && (
                <span
                  role="status"
                  className="oss-address-check oss-address-check--checking"
                >
                  {t(
                    "messages.checkingAddress",
                    "Checking this address against existing OSS organisations...",
                  )}
                </span>
              )}
            {addressAvailability.placeId === form.company_address_place_id &&
              addressAvailability.status === "available" &&
              !fieldErrors.company_address && (
                <span
                  role="status"
                  className="oss-address-check oss-address-check--available"
                >
                  {t(
                    "messages.addressAvailable",
                    "Address verified and available.",
                  )}
                </span>
              )}
            {fieldErrors.company_address && (
              <span role="alert">{fieldErrors.company_address}</span>
            )}
            {!fieldErrors.company_address &&
              addressAvailability.placeId === form.company_address_place_id &&
              ["duplicate", "error"].includes(addressAvailability.status) && (
                <span
                  role="alert"
                  className="oss-address-check oss-address-check--error"
                >
                  {addressAvailability.message}
                </span>
              )}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.country")}
              <span className="oss-required">*</span>
            </label>
            <select
              name="country_code"
              value={form.country_code}
              onChange={onChange}
              disabled={prefilledFields.country_code}
              className={prefilledFields.country_code ? "oss-prefilled" : ""}
            >
              <option value="">{t("placeholders.country")}</option>
              <option value="BE">{t("countries.belgium")}</option>
              <option value="ES">{t("countries.spain")}</option>
            </select>
            {fieldErrors.country_code && (
              <span>{fieldErrors.country_code}</span>
            )}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.vatNumber")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="vat_number"
              value={form.vat_number}
              onChange={onChange}
              placeholder={t("placeholders.vatNumber")}
              className={
                fieldErrors.vat_number ? "oss-company-input-error" : ""
              }
            />
            {fieldErrors.vat_number && <span>{fieldErrors.vat_number}</span>}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.roleInCompany")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="role_in_company"
              value={form.role_in_company}
              onChange={onChange}
              readOnly={prefilledFields.role_in_company}
              className={prefilledFields.role_in_company ? "oss-prefilled" : ""}
            />
            {fieldErrors.role_in_company && (
              <span>{fieldErrors.role_in_company}</span>
            )}
          </div>

          <div className="oss-company-field">
            <label>
              {t("fields.phone")}
              <span className="oss-required">*</span>
            </label>
            <input
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder={t("placeholders.phone")}
              readOnly={prefilledFields.phone}
              className={prefilledFields.phone ? "oss-prefilled" : ""}
            />
            {fieldErrors.phone && <span>{fieldErrors.phone}</span>}
          </div>
        </div>

        <div className="oss-company-field oss-company-full">
          <label>{t("fields.additionalNotes")}</label>
          <textarea
            name="additional_notes"
            rows="4"
            value={form.additional_notes}
            onChange={onChange}
            placeholder={t("placeholders.additionalNotes")}
          />
        </div>

        {err && (
          <div
            className="oss-company-message error"
            style={{
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              maxHeight: "none",
              height: "auto",
              overflow: "visible",
            }}
          >
            {err}
          </div>
        )}

        <div className="oss-company-footer">
          <p>{t("footer.help")}</p>
          <button
            type="submit"
            disabled={
              isSubmitting ||
              loadingPreview ||
              addressAvailability.status === "checking" ||
              (addressAvailability.placeId === form.company_address_place_id &&
                addressAvailability.status === "duplicate")
            }
          >
            {isSubmitting
              ? t("messages.checkingAddress", "Checking address...")
              : t("buttons.continue")}
          </button>
        </div>
      </form>
    </div>
  );
}
